import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment-timezone';
import ReactApexChart from 'react-apexcharts';
import { FLEET_EVENT_TIMELINE_COLORS } from './fuelLevelSignalUtils';
import './FleetEventTimelinePanel.css';

const CHART_ID = 'fleet-event-timeline-chart';
const ROW_HEIGHT_PX = 36;
const EVENT_DETAIL_WIDTH_PX = 272;
const EVENT_DETAIL_HEIGHT_PX = 216;
const EVENT_DETAIL_OFFSET_PX = 12;

const formatTimelineTime = (value, timezone) => (
  moment(value).tz(timezone).format('YYYY-MM-DD HH:mm:ss [UTC]Z')
);

const formatDeltaLitres = (value) => {
  if (value == null || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  const prefix = numeric > 0 ? '+' : '';
  return `${prefix}${numeric.toFixed(1)} L`;
};

const parseCoordinates = (latitude, longitude) => {
  if (latitude == null || latitude === '' || longitude == null || longitude === '') return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const formatLocation = (latitude, longitude) => {
  const coordinates = parseCoordinates(latitude, longitude);
  if (!coordinates) return null;
  return `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`;
};

const buildGoogleMapsUrl = (latitude, longitude) => {
  const coordinates = parseCoordinates(latitude, longitude);
  if (!coordinates) return null;
  return `https://maps.google.com/?q=${encodeURIComponent(`${coordinates.lat},${coordinates.lng}`)}`;
};

const FleetEventTimelinePanel = ({
  timelineView = null,
  loading = false,
  error = '',
  timezone = 'Asia/Kuala_Lumpur',
  filterRangeStart = null,
  filterRangeEnd = null,
  onAssetClick = null,
  emptyMessage = 'No fuel assets available for the fleet event timeline.',
}) => {
  const [zoomRange, setZoomRange] = useState({ min: null, max: null });
  const [assetLaneTops, setAssetLaneTops] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const panelRef = useRef(null);
  const eventDetailRef = useRef(null);

  const assets = timelineView?.assets || [];
  const chartHeight = Math.max(180, assets.length * ROW_HEIGHT_PX + 72);

  const syncAssetLanePositions = useCallback((chartContext) => {
    const globals = chartContext?.w?.globals;
    const yAxisConfig = chartContext?.w?.config?.yaxis;
    const yAxis = Array.isArray(yAxisConfig) ? yAxisConfig[0] : yAxisConfig;
    if (!globals || assets.length === 0) return;

    const plotTop = globals.translateY;
    const plotHeight = globals.gridHeight;
    const laneMin = -0.5;
    const laneMax = assets.length - 0.5;
    const laneRange = laneMax - laneMin;
    const reversed = yAxis?.reversed === true;

    setAssetLaneTops(
      assets.map((_, lane) => {
        const ratio = (lane - laneMin) / laneRange;
        return reversed
          ? plotTop + ratio * plotHeight
          : plotTop + (1 - ratio) * plotHeight;
      }),
    );
  }, [assets]);

  useEffect(() => {
    setZoomRange({ min: null, max: null });
    setSelectedEvent(null);
  }, [timelineView, filterRangeStart, filterRangeEnd]);

  useEffect(() => {
    if (!selectedEvent?.pinned) return undefined;

    eventDetailRef.current?.focus();

    const handleDocumentPointerDown = (event) => {
      const isInsideDetails = eventDetailRef.current?.contains(event.target);
      const isInsideChart = chartContainerRef.current?.contains(event.target);
      if (!isInsideDetails && !isInsideChart) setSelectedEvent(null);
    };
    const handleDocumentKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedEvent(null);
    };

    document.addEventListener('mousedown', handleDocumentPointerDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentPointerDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [selectedEvent?.pinned, selectedEvent?.point]);

  useEffect(() => {
    if (!timelineView?.hasEvents || filterRangeStart == null || filterRangeEnd == null) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const chart = chartRef.current?.chart;
      chart?.zoomX(filterRangeStart, filterRangeEnd);
      if (chart) syncAssetLanePositions(chart);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [timelineView, filterRangeStart, filterRangeEnd, syncAssetLanePositions]);

  useEffect(() => {
    setAssetLaneTops([]);
  }, [timelineView?.assets, chartHeight]);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      const chart = chartRef.current?.chart;
      if (chart) syncAssetLanePositions(chart);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [syncAssetLanePositions, timelineView?.hasEvents]);

  const isZoomed = zoomRange.min != null && zoomRange.max != null;

  const handleResetZoom = () => {
    setSelectedEvent(null);
    setZoomRange({ min: null, max: null });
    const chart = chartRef.current?.chart;
    if (chart && filterRangeStart != null && filterRangeEnd != null) {
      chart.zoomX(filterRangeStart, filterRangeEnd);
      return;
    }
    chart?.resetSeries();
  };

  const showEventDetails = useCallback((event, selection, pinned) => {
    const point = selection?.w?.config?.series?.[selection.seriesIndex]
      ?.data?.[selection.dataPointIndex];
    if (!point) return;

    const panelRect = panelRef.current?.getBoundingClientRect();
    const pointerX = panelRect && Number.isFinite(event?.clientX)
      ? event.clientX - panelRect.left
      : null;
    const pointerY = panelRect && Number.isFinite(event?.clientY)
      ? event.clientY - panelRect.top
      : null;

    let left = pointerX == null ? 8 : pointerX + EVENT_DETAIL_OFFSET_PX;
    let top = pointerY == null ? 64 : pointerY + EVENT_DETAIL_OFFSET_PX;
    if (panelRect) {
      if (left + EVENT_DETAIL_WIDTH_PX > panelRect.width - 8) {
        left = Math.max(8, (pointerX ?? panelRect.width) - EVENT_DETAIL_WIDTH_PX - EVENT_DETAIL_OFFSET_PX);
      }
      if (top + EVENT_DETAIL_HEIGHT_PX > panelRect.height - 8) {
        top = Math.max(8, (pointerY ?? panelRect.height) - EVENT_DETAIL_HEIGHT_PX - EVENT_DETAIL_OFFSET_PX);
      }
    }

    setSelectedEvent((current) => {
      if (!pinned && current?.pinned) return current;
      return {
        point, left, top, pinned,
      };
    });
  }, []);

  const clearHoveredEvent = useCallback(() => {
    setSelectedEvent((current) => (current?.pinned ? current : null));
  }, []);

  const chartSeries = useMemo(
    () => (timelineView?.series || []).map((entry) => ({
      name: entry.name,
      data: entry.data.map((point) => ({
        x: point.x,
        y: point.y,
        asset: point.asset,
        deviceOid: point.deviceOid,
        event: point.event,
        eventFilterKey: point.eventFilterKey,
        deltaLitres: point.deltaLitres,
        levelAfter: point.levelAfter,
        time: point.time,
        latitude: point.latitude,
        longitude: point.longitude,
        motionLabel: point.motionLabel,
      })),
    })),
    [timelineView],
  );

  const chartColors = useMemo(
    () => chartSeries.map((entry) => FLEET_EVENT_TIMELINE_COLORS[entry.name] || '#667085'),
    [chartSeries],
  );

  const chartOptions = useMemo(() => ({
    chart: {
      id: CHART_ID,
      type: 'scatter',
      toolbar: { show: false },
      fontFamily: 'Inter',
      animations: { enabled: false },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: false,
      },
      selection: {
        enabled: true,
        type: 'x',
        fill: {
          color: '#00868B',
          opacity: 0.12,
        },
        stroke: {
          width: 1,
          color: '#00868B',
          opacity: 0.5,
          dashArray: 4,
        },
      },
      events: {
        mounted(chartContext) {
          syncAssetLanePositions(chartContext);
        },
        updated(chartContext) {
          syncAssetLanePositions(chartContext);
        },
        zoomed(chartContext, { xaxis }) {
          setSelectedEvent(null);
          if (xaxis?.min != null && xaxis?.max != null) {
            setZoomRange({ min: xaxis.min, max: xaxis.max });
          }
          syncAssetLanePositions(chartContext);
        },
        scrolled(chartContext, { xaxis }) {
          setSelectedEvent(null);
          if (xaxis?.min != null && xaxis?.max != null) {
            setZoomRange({ min: xaxis.min, max: xaxis.max });
          }
          syncAssetLanePositions(chartContext);
        },
        click(event, chartContext, selection) {
          if (selection?.dataPointIndex == null || selection.dataPointIndex < 0) {
            setSelectedEvent(null);
          }
        },
        dataPointMouseEnter(event, chartContext, selection) {
          showEventDetails(event, selection, false);
        },
        dataPointMouseLeave() {
          clearHoveredEvent();
        },
        dataPointSelection(event, chartContext, selection) {
          showEventDetails(event, selection, true);
        },
      },
    },
    colors: chartColors,
    markers: {
      size: 6,
      strokeWidth: 0,
      hover: { size: 8 },
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: '#EAECF0',
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: {
        left: 8,
        right: 12,
      },
    },
    xaxis: {
      type: 'datetime',
      min: zoomRange.min ?? filterRangeStart ?? undefined,
      max: zoomRange.max ?? filterRangeEnd ?? undefined,
      labels: {
        formatter: (value) => moment(value).format('YYYY-MM-DD'),
        style: { colors: '#667085', fontSize: '11px' },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: assets.length > 0 ? -0.5 : 0,
      max: assets.length > 0 ? assets.length - 0.5 : 1,
      tickAmount: Math.max(assets.length, 1),
      reversed: assets.length > 0,
      labels: { show: false },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'left',
      markers: {
        width: 8,
        height: 8,
        radius: 8,
      },
    },
    tooltip: { enabled: false },
  }), [
    assets.length,
    chartColors,
    clearHoveredEvent,
    filterRangeEnd,
    filterRangeStart,
    showEventDetails,
    syncAssetLanePositions,
    zoomRange.max,
    zoomRange.min,
  ]);

  const selectedPoint = selectedEvent?.point || null;
  const selectedDelta = selectedPoint ? formatDeltaLitres(selectedPoint.deltaLitres) : '';
  const selectedLocation = selectedPoint
    ? formatLocation(selectedPoint.latitude, selectedPoint.longitude)
    : null;
  const selectedMapsUrl = selectedPoint
    ? buildGoogleMapsUrl(selectedPoint.latitude, selectedPoint.longitude)
    : null;
  const selectedLevelAfter = selectedPoint?.levelAfter == null
    || selectedPoint.levelAfter === ''
    ? null
    : Number(selectedPoint.levelAfter);
  let selectedLocationDetail = (
    <div className="fleet-event-detail-meta">Location unavailable</div>
  );
  if (selectedLocation) {
    selectedLocationDetail = selectedEvent?.pinned && selectedMapsUrl ? (
      <a
        className="fleet-event-detail-location"
        href={selectedMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in Google Maps"
      >
        {selectedLocation}
        {' '}
        ↗
      </a>
    ) : (
      <div className="fleet-event-detail-meta">{selectedLocation}</div>
    );
  }

  if (loading) {
    return (
      <div className="fleet-event-timeline-panel">
        <div className="fleet-event-timeline-loading">Loading fleet event timeline…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fleet-event-timeline-panel">
        <div className="fleet-event-timeline-error" role="alert">{error}</div>
      </div>
    );
  }

  if (!timelineView || assets.length === 0) {
    return (
      <div className="fleet-event-timeline-panel">
        <div className="fleet-event-timeline-empty">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="fleet-event-timeline-panel" ref={panelRef}>
      <div className="fuel-analytics-chart-header-row">
        <div>
          <h3 className="fuel-analytics-chart-title">Fleet event timeline</h3>
          <p className="fuel-analytics-chart-subtitle">
            refills · drains · alerts — click a dot for details, click a label to open the vehicle
          </p>
        </div>
        {isZoomed && (
          <div className="fuel-analytics-chart-zoom-controls">
            <span className="fuel-analytics-chart-zoom-range">
              Showing
              {' '}
              {moment(zoomRange.min).tz(timezone).format('MMM DD, YYYY')}
              {' '}
              –
              {' '}
              {moment(zoomRange.max).tz(timezone).format('MMM DD, YYYY')}
            </span>
            <button
              type="button"
              className="fuel-analytics-chart-reset-zoom"
              onClick={handleResetZoom}
            >
              Reset zoom
            </button>
          </div>
        )}
      </div>

      <div className="fuel-analytics-chart-toolbar">
        <span className="fuel-analytics-chart-hint">Drag on the chart to zoom by time</span>
        {!isZoomed && filterRangeStart != null && filterRangeEnd != null && (
          <span className="fuel-analytics-chart-zoom-range">
            Date range:
            {' '}
            {moment(filterRangeStart).tz(timezone).format('MMM DD, YYYY')}
            {' '}
            –
            {' '}
            {moment(filterRangeEnd).tz(timezone).format('MMM DD, YYYY')}
          </span>
        )}
      </div>

      {!timelineView.hasEvents ? (
        <div className="fleet-event-timeline-empty">
          No refill, drain, or alert events in this date range.
        </div>
      ) : (
        <div className="fleet-event-timeline-layout" style={{ minHeight: chartHeight }}>
          <div
            className="fleet-event-timeline-assets"
            style={{ height: chartHeight }}
          >
            {assets.map((asset, index) => {
              const laneTop = assetLaneTops[index];
              return (
                <button
                  key={asset}
                  type="button"
                  className="fleet-event-timeline-asset-btn"
                  style={laneTop == null ? undefined : {
                    top: `${laneTop - (ROW_HEIGHT_PX / 2)}px`,
                  }}
                  onClick={() => {
                    setSelectedEvent(null);
                    onAssetClick?.(asset);
                  }}
                  title={`Open ${asset}`}
                >
                  {asset}
                </button>
              );
            })}
          </div>
          <div className="fleet-event-timeline-chart" ref={chartContainerRef}>
            <ReactApexChart
              ref={chartRef}
              options={chartOptions}
              series={chartSeries}
              type="scatter"
              height={chartHeight}
            />
          </div>
        </div>
      )}
      {selectedPoint && (
        <div
          ref={eventDetailRef}
          className={`fleet-event-detail-modal ${selectedEvent.pinned ? 'is-pinned' : 'is-hover'}`}
          role={selectedEvent.pinned ? 'dialog' : 'tooltip'}
          aria-labelledby={`${CHART_ID}-detail-title`}
          tabIndex={selectedEvent.pinned ? -1 : undefined}
          style={{ left: selectedEvent.left, top: selectedEvent.top }}
        >
          {selectedEvent.pinned && (
            <button
              type="button"
              className="fleet-event-detail-close"
              aria-label="Close event details"
              onClick={() => setSelectedEvent(null)}
            >
              ×
            </button>
          )}
          <div id={`${CHART_ID}-detail-title`} className="fleet-event-detail-title">
            {selectedPoint.asset}
            {' · '}
            {formatTimelineTime(selectedPoint.x, timezone)}
          </div>
          <div className="fleet-event-detail-event-row">
            <span className={`fleet-event-detail-event fleet-event-detail-event-${String(selectedPoint.eventFilterKey || '').toLowerCase()}`}>
              {selectedPoint.event}
            </span>
            {selectedDelta && <span>{selectedDelta}</span>}
          </div>
          {selectedLevelAfter != null && Number.isFinite(selectedLevelAfter) && (
            <div className="fleet-event-detail-meta">
              Level after:
              {' '}
              {selectedLevelAfter.toFixed(2)}
              {' '}
              L
            </div>
          )}
          {selectedPoint.motionLabel && (
            <div className="fleet-event-detail-meta">{selectedPoint.motionLabel}</div>
          )}
          {selectedLocationDetail}
        </div>
      )}
    </div>
  );
};

export default FleetEventTimelinePanel;
